import { useState, useEffect, useRef } from "react";

/**
 * Custom hook to manage data fetching for FleetBreakdownChart
 * @param {Object} options - Options for the hook
 * @param {Array} options.dependencies - Dependencies (startDate, endDate, globalFilters)
 * @param {string} options.groupBy - The current group by field
 * @param {Object} options.config - Chart configuration with fetchers and transformers
 * @returns {Object} - State and handlers for the chart data
 */
export const useFleetBreakdownData = ({ dependencies, groupBy, config }) => {
    const [groupByItems, setGroupByItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [loadingGroupBy, setLoadingGroupBy] = useState(false);
    const [loadingChart, setLoadingChart] = useState(false);

    const isInitialLoadRef = useRef(true);
    const dependenciesJson = JSON.stringify(dependencies);

    // 1. Fetch Sidebar Data (when groupBy or global dependencies change)
    useEffect(() => {
        let isActive = true;

        const fetchSidebarData = async () => {
            setLoadingGroupBy(true);
            try {
                if (config.onFetchSidebarData) {
                    const { items } = await config.onFetchSidebarData(groupBy);
                    if (!isActive) return;

                    setGroupByItems(items);

                    // Determine initial selection
                    let initialSelection = [];
                    if (items.length > 0) {
                        const allItem = items.find(i => i.id === 'all');
                        if (allItem) {
                            initialSelection = [allItem];
                        } else {
                            initialSelection = [items[0]];
                        }
                    }
                    setSelectedItems(initialSelection);
                }
            } catch (error) {
                console.error("Error fetching sidebar data:", error);
                if (isActive) {
                    setGroupByItems([]);
                    setSelectedItems([]);
                }
            } finally {
                if (isActive) {
                    setLoadingGroupBy(false);
                    isInitialLoadRef.current = false;
                }
            }
        };

        fetchSidebarData();

        return () => { isActive = false; };
    }, [groupBy, dependenciesJson, config.onFetchSidebarData]);

    // 2. Fetch Chart Data (when selection or groupBy changes)
    useEffect(() => {
        // Skip fetching if sidebar is still loading or if it's the very first load 
        // (to avoid double fetch if Effect 1 is also running)
        if (loadingGroupBy || isInitialLoadRef.current) return;

        let isActive = true;

        const fetchChartData = async () => {
            if (selectedItems.length === 0) {
                setChartData([]);
                setLoadingChart(false);
                return;
            }

            setLoadingChart(true);

            try {
                if (config.onFetchChartData) {
                    const rawChartData = await config.onFetchChartData(groupBy, selectedItems);
                    if (!isActive) return;

                    const transformedData = config.transformChartData ? config.transformChartData(rawChartData) : rawChartData;
                    setChartData(transformedData);
                }
            } catch (error) {
                console.error("Error fetching chart data:", error);
                if (isActive) setChartData([]);
            } finally {
                if (isActive) setLoadingChart(false);
            }
        };

        fetchChartData();

        return () => { isActive = false; };
    }, [groupBy, selectedItems, dependenciesJson, config.onFetchChartData, config.transformChartData, loadingGroupBy]);

    return {
        groupByItems,
        setGroupByItems,
        selectedItems,
        setSelectedItems,
        chartData,
        setChartData,
        loadingGroupBy,
        loadingChart
    };
};
